/**
 * Custom rankers tab for the Ranking Evaluation structure.
 *
 * Displays a table of custom LLM-driven ranker configurations. Evaluators can
 * add or remove ranker entries and trigger a server-side re-ranking pass
 * against the currently fetched conversations.
 */

import { useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import { Banner } from 'component/banner';
import { HorizBox, Pad, VertBox } from 'component/basics';
import { CTAButton, SubtleButton } from 'component/button';
import type { TableColumn, TableColumnWidgetProps } from 'component/dashboard/table-grid';
import { TableGrid } from 'component/dashboard/table-grid';
import { DropdownField } from 'component/form';
import { Checkbox } from 'component/form/checkbox';
import { LoadingSpinnerContainer } from 'component/progress/loading-spinner';
import { Heading, TextField, UtilityText } from 'component/text';
import type { EvalCustomRanker } from 'data/ranking-eval';
import {
    deleteEvalCustomRankerAsync,
    saveEvalCustomRankerAsync,
    useEvalCustomRankers,
    useEvalRankerDetails,
    useEvalRankerList,
} from 'data/ranking-eval';
import { useMemo, useState } from 'react';
import { View } from 'react-native';
import { useDatastore } from 'util/datastore';
import type { TLabel } from 'util/stdtypes';
import { t } from 'util/stdtypes';

// ============================================================================
// CUSTOM RANKERS TAB
// ============================================================================

const MODEL_OPTIONS = [
    { key: 'gpt-5.4-mini', text: 'GPT 5.4 Mini' },
    { key: 'gpt-5.4', text: 'GPT 5.4' },
];

const REASONING_OPTIONS = [
    { key: 'low', text: 'Low' },
    { key: 'medium', text: 'Medium' },
    { key: 'high', text: 'High' },
];

// Labeled text field helper
function LabeledField({ label, children }: { label: TLabel; children: React.ReactNode }) {
    return (
        <VertBox gap={4} wide>
            <UtilityText label={label} type='small' weight='medium' />
            {children}
        </VertBox>
    );
}

// Model/N/reasoning controls row
function RankerModelControls({
    topN,
    onTopNChange,
    model,
    onModelChange,
    reasoningEffort,
    onReasoningChange,
}: {
    topN: string;
    onTopNChange: (v: string) => void;
    model: string;
    onModelChange: (v: string) => void;
    reasoningEffort: string;
    onReasoningChange: (v: string) => void;
}) {
    return (
        <HorizBox center gap={16}>
            <View style={{ width: 100 }}>
                <UtilityText label={t('ranking-eval.top_n', 'Top N')} type='small' weight='medium' />
                <Pad size={4} />
                <TextField onChange={onTopNChange} placeholderLabel={t('ranking-eval.top_n_placeholder', '20')} value={topN} />
            </View>
            <View style={{ minWidth: 180 }}>
                <UtilityText label={t('ranking-eval.model', 'Model')} type='small' weight='medium' />
                <Pad size={4} />
                <DropdownField
                    onChange={onModelChange}
                    options={MODEL_OPTIONS}
                    placeholder={t('ranking-eval.model_placeholder', 'Select model')}
                    value={model}
                />
            </View>
            <View style={{ minWidth: 180 }}>
                <UtilityText label={t('ranking-eval.reasoning', 'Thinking Level')} type='small' weight='medium' />
                <Pad size={4} />
                <DropdownField
                    onChange={onReasoningChange}
                    options={REASONING_OPTIONS}
                    placeholder={t('ranking-eval.reasoning_placeholder', 'Select level')}
                    value={reasoningEffort}
                />
            </View>
        </HorizBox>
    );
}

type CustomRankerEditorProps = {
    ranker: EvalCustomRanker;
    onSave: (ranker: EvalCustomRanker) => void;
    onCancel: () => void;
    onDelete?: () => void;
};

function CustomRankerEditor({ ranker, onSave, onCancel, onDelete }: CustomRankerEditorProps) {
    const [name, setName] = useState(ranker.name);
    const [description, setDescription] = useState(ranker.description);
    const [firstPassPrompt, setFirstPassPrompt] = useState(ranker.firstPassPrompt);
    const [secondPassPrompt, setSecondPassPrompt] = useState(ranker.secondPassPrompt);
    const [topN, setTopN] = useState(ranker.topN.toString());
    const [model, setModel] = useState(ranker.model);
    const [reasoningEffort, setReasoningEffort] = useState(ranker.reasoningEffort);
    const [enabled, setEnabled] = useState(ranker.enabled);

    function handleSave() {
        onSave({
            ...ranker,
            name,
            description,
            firstPassPrompt,
            secondPassPrompt,
            topN: parseInt(topN, 10) || 20,
            model,
            reasoningEffort,
            enabled,
        });
    }

    return (
        <VertBox gap={16} wide>
            <Heading label={ranker.key ? t('ranking-eval.edit_ranker', 'Edit Ranker') : t('ranking-eval.new_ranker', 'New Ranker')} />
            <LabeledField label={t('ranking-eval.ranker_name', 'Name')}>
                <TextField
                    onChange={setName}
                    placeholderLabel={t('ranking-eval.ranker_name_placeholder', 'e.g. Constructive Debate')}
                    value={name}
                />
            </LabeledField>
            <LabeledField label={t('ranking-eval.ranker_description', 'Description')}>
                <TextField
                    onChange={setDescription}
                    placeholderLabel={t('ranking-eval.ranker_desc_placeholder', 'Brief description')}
                    value={description}
                />
            </LabeledField>
            <LabeledField label={t('ranking-eval.first_pass_prompt', 'First Pass Prompt')}>
                <TextField
                    multiline
                    onChange={setFirstPassPrompt}
                    placeholderLabel={t('ranking-eval.first_pass_placeholder', 'Use {{question}} and {{commentText}}')}
                    value={firstPassPrompt}
                />
            </LabeledField>
            <LabeledField label={t('ranking-eval.second_pass_prompt', 'Second Pass Prompt')}>
                <TextField
                    multiline
                    onChange={setSecondPassPrompt}
                    placeholderLabel={t('ranking-eval.second_pass_placeholder', 'Use {{question}} and {{comments}}')}
                    value={secondPassPrompt}
                />
            </LabeledField>
            <RankerModelControls
                model={model}
                onModelChange={setModel}
                onReasoningChange={setReasoningEffort}
                onTopNChange={setTopN}
                reasoningEffort={reasoningEffort}
                topN={topN}
            />
            <Checkbox label={t('ranking-eval.ranker_enabled', 'Enabled')} onChange={() => setEnabled(!enabled)} value={enabled} />
            <HorizBox gap={8}>
                <CTAButton label={t('ranking-eval.save_ranker', 'Save')} onPress={handleSave} type='primary' />
                <CTAButton label={t('ranking-eval.cancel', 'Cancel')} onPress={onCancel} type='secondary' />
                {onDelete && <CTAButton label={t('ranking-eval.delete_ranker', 'Delete')} onPress={onDelete} type='secondary' />}
            </HorizBox>
        </VertBox>
    );
}

// ============================================================================
// RANKER TABLE TYPES AND COLUMNS
// ============================================================================

type RankerTableItem = {
    key: string;
    name: string;
    description: string;
    model?: string;
    reasoningEffort?: string;
    topN?: number;
    enabled?: boolean;
    isBuiltIn?: boolean;
};

const DEFAULT_FIRST_PASS_PROMPT = `You are scoring a comment for [describe what you are looking for].

Question/Topic: {{question}}

Comment: {{commentText}}

Score this comment on a 1-5 scale using the following rubric:

1 - [Worst]: [Describe what a score of 1 looks like]
2 - [Poor]: [Describe what a score of 2 looks like]
3 - [Adequate]: [Describe what a score of 3 looks like]
4 - [Good]: [Describe what a score of 4 looks like]
5 - [Exemplary]: [Describe what a score of 5 looks like]

Key qualities to reward:
- [Quality 1]
- [Quality 2]
- [Quality 3]

Respond with JSON: { "score": N }
{{#includeReasoning}}Also include a "reasoning" field explaining your score.{{/includeReasoning}}`;

const DEFAULT_SECOND_PASS_PROMPT = `You are selecting the top 10 comments for a discussion.

Question/Topic: {{question}}

Here are the top-scoring comments from the first pass (JSON array of {key, text, score}):
{{comments}}

Select exactly 10 comments that together form the best possible "top 10" for this discussion. Optimize for:

1. Quality: Prefer comments with higher first-pass scores, but use your judgment to override when appropriate.
2. Viewpoint diversity: Ensure multiple perspectives and positions are represented.
3. [Add your own criteria here]

Respond with JSON: { "ranking": ["key1", "key2", ...] }
The array should be ordered from best to last, with the most exemplary comment first.
{{#includeReasoning}}Also include a "reasoning" field explaining your ranking choices.{{/includeReasoning}}`;

const EMPTY_RANKER: EvalCustomRanker = {
    key: '',
    name: '',
    description: '',
    firstPassPrompt: DEFAULT_FIRST_PASS_PROMPT,
    secondPassPrompt: DEFAULT_SECOND_PASS_PROMPT,
    topN: 20,
    model: 'gpt-5.4-mini',
    reasoningEffort: 'low',
    enabled: true,
};

// Shared table columns for ranker tables

function RankerNameWidget({ item }: TableColumnWidgetProps<RankerTableItem>) {
    const { colors } = useTheme();
    return (
        <VertBox gap={2}>
            <HorizBox center gap={8}>
                <UtilityText text={item.name} type='small' weight='medium' />
                {item.enabled === false && (
                    <UtilityText color={colors.colorTextSecondary} label={t('ranking-eval.disabled', '(disabled)')} type='tiny' />
                )}
            </HorizBox>
            <UtilityText color={colors.colorTextSecondary} text={item.description} type='tiny' />
        </VertBox>
    );
}

const RankerNameColumn: TableColumn<RankerTableItem> = {
    label: t('ranking-eval.ranker_col.name', 'Name'),
    widget: RankerNameWidget,
    span: 3,
};

function RankerModelWidget({ item }: TableColumnWidgetProps<RankerTableItem>) {
    return <UtilityText text={item.model || '—'} type='small' />;
}

const RankerModelColumn: TableColumn<RankerTableItem> = {
    label: t('ranking-eval.ranker_col.model', 'Model'),
    widget: RankerModelWidget,
};

function RankerThinkingWidget({ item }: TableColumnWidgetProps<RankerTableItem>) {
    return <UtilityText text={item.reasoningEffort || '—'} type='small' />;
}

const RankerThinkingColumn: TableColumn<RankerTableItem> = {
    label: t('ranking-eval.ranker_col.thinking', 'Thinking'),
    widget: RankerThinkingWidget,
};

function RankerTopNWidget({ item }: TableColumnWidgetProps<RankerTableItem>) {
    return <UtilityText text={item.topN != null ? String(item.topN) : '—'} type='small' />;
}

const RankerTopNColumn: TableColumn<RankerTableItem> = {
    label: t('ranking-eval.ranker_col.topn', 'Top N'),
    widget: RankerTopNWidget,
};

// Action columns differ between custom (Edit) and built-in (View) — created
// per-table via callbacks

function customRankerToTableItem(ranker: EvalCustomRanker): RankerTableItem {
    return { ...ranker, isBuiltIn: false };
}

function builtInToTableItem(ranker: { key: string; name: string; description: string }): RankerTableItem {
    return { key: ranker.key, name: ranker.name, description: ranker.description, isBuiltIn: true };
}

function CustomRankerList({
    rankers,
    onCreate,
    onEdit,
}: {
    rankers: EvalCustomRanker[];
    onCreate: () => void;
    onEdit: (key: string) => void;
}) {
    const { colors } = useTheme();
    const editColumn = useMemo((): TableColumn<RankerTableItem> => {
        // eslint-disable-next-line react/no-unstable-nested-components -- stabilized by useMemo; named for DevTools
        function EditWidget({ item }: TableColumnWidgetProps<RankerTableItem>) {
            return <SubtleButton label={t('ranking-eval.edit', 'Edit')} onPress={() => onEdit(item.key)} />;
        }
        return {
            label: t('ranking-eval.ranker_col.actions', 'Actions'),
            alignRight: true,
            widget: EditWidget,
        };
    }, [onEdit]);

    const columns = [RankerNameColumn, RankerModelColumn, RankerThinkingColumn, RankerTopNColumn, editColumn];
    const items = rankers.map(customRankerToTableItem);

    return (
        <VertBox gap={16} wide>
            <HorizBox center spread wide>
                <Heading label={t('ranking-eval.custom_rankers_title', 'Custom Rankers')} />
                <CTAButton label={t('ranking-eval.add_ranker', 'Add Ranker')} onPress={onCreate} type='secondary' />
            </HorizBox>
            <UtilityText
                color={colors.colorTextSecondary}
                label={t(
                    'ranking-eval.custom_rankers_desc',
                    'Define custom LLM-driven rankers for use in side-by-side evaluation. Custom rankers appear in the ranker dropdown.',
                )}
                type='small'
            />
            <TableGrid
                columns={columns}
                emptyWidget={
                    <Banner>
                        <UtilityText
                            label={t('ranking-eval.no_custom_rankers', 'No custom rankers yet. Click "Add Ranker" to create one.')}
                            type='small'
                        />
                    </Banner>
                }
                itemList={items}
            />
        </VertBox>
    );
}

// Read-only viewer for a built-in ranker's prompts
function BuiltInRankerViewer({ rankerKey, onBack }: { rankerKey: string; onBack: () => void }) {
    const ranker = useEvalRankerDetails(rankerKey);
    const { colors } = useTheme();
    const s = useReadOnlyPromptStyle();

    if (!ranker) {
        return <LoadingSpinnerContainer label={t('ranking-eval.loading_ranker', 'Loading ranker...')} />;
    }

    return (
        <VertBox gap={16} wide>
            <Heading text={ranker.name} />
            <UtilityText color={colors.colorTextSecondary} text={ranker.description} type='small' />
            {ranker.firstPassPrompt && (
                <LabeledField label={t('ranking-eval.first_pass_prompt', 'First Pass Prompt')}>
                    <View style={s.box}>
                        <UtilityText text={ranker.firstPassPrompt} type='small' />
                    </View>
                </LabeledField>
            )}
            {ranker.secondPassPrompt && (
                <LabeledField label={t('ranking-eval.second_pass_prompt', 'Second Pass Prompt')}>
                    <View style={s.box}>
                        <UtilityText text={ranker.secondPassPrompt} type='small' />
                    </View>
                </LabeledField>
            )}
            <CTAButton label={t('ranking-eval.back', 'Back')} onPress={onBack} type='secondary' />
        </VertBox>
    );
}

const useReadOnlyPromptStyle = createThemedStyles((tokens) => ({
    box: {
        backgroundColor: tokens.colors.colorSurfaceSecondary,
        borderRadius: 8,
        padding: 16,
    },
}));

function BuiltInRankerTable({
    rankers,
    onView,
}: {
    rankers: { key: string; name: string; description: string }[];
    onView: (key: string) => void;
}) {
    const { colors } = useTheme();
    const viewColumn = useMemo((): TableColumn<RankerTableItem> => {
        // eslint-disable-next-line react/no-unstable-nested-components -- stabilized by useMemo; named for DevTools
        function ViewWidget({ item }: TableColumnWidgetProps<RankerTableItem>) {
            return <SubtleButton label={t('ranking-eval.view', 'View')} onPress={() => onView(item.key)} />;
        }
        return {
            label: t('ranking-eval.ranker_col.actions', 'Actions'),
            alignRight: true,
            widget: ViewWidget,
        };
    }, [onView]);

    const columns = [RankerNameColumn, viewColumn];
    const items = rankers.map(builtInToTableItem);

    return (
        <VertBox gap={16} wide>
            <Heading label={t('ranking-eval.builtin_rankers_title', 'Built-in Rankers')} />
            <UtilityText
                color={colors.colorTextSecondary}
                label={t('ranking-eval.builtin_rankers_desc', 'View the prompts of built-in rankers for reference when creating your own.')}
                type='small'
            />
            <TableGrid columns={columns} itemList={items} />
        </VertBox>
    );
}

export function CustomRankersTab() {
    const datastore = useDatastore();
    const rankers = useEvalCustomRankers();
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [viewingBuiltIn, setViewingBuiltIn] = useState<string | null>(null);

    // Fetch built-in ranker list from server
    const builtInRankerList = useEvalRankerList();
    const builtInRankers = builtInRankerList.filter((r) => r.key !== 'most-reacted' && r.key !== 'e2e-test');

    async function handleSave(ranker: EvalCustomRanker) {
        await saveEvalCustomRankerAsync(datastore, { ranker });
        setEditingKey(null);
        setIsCreating(false);
    }

    async function handleDelete(key: string) {
        await deleteEvalCustomRankerAsync(datastore, { rankerKey: key });
        setEditingKey(null);
    }

    if (viewingBuiltIn) {
        return <BuiltInRankerViewer onBack={() => setViewingBuiltIn(null)} rankerKey={viewingBuiltIn} />;
    }

    if (isCreating) {
        return <CustomRankerEditor onCancel={() => setIsCreating(false)} onSave={handleSave} ranker={EMPTY_RANKER} />;
    }

    if (editingKey) {
        const ranker = rankers.find((r) => r.key === editingKey);
        if (!ranker) return null;
        return (
            <CustomRankerEditor
                onCancel={() => setEditingKey(null)}
                onDelete={() => handleDelete(editingKey)}
                onSave={handleSave}
                ranker={ranker}
            />
        );
    }

    return (
        <VertBox gap={32} wide>
            <CustomRankerList
                onCreate={() => {
                    setIsCreating(true);
                    setEditingKey(null);
                }}
                onEdit={setEditingKey}
                rankers={rankers}
            />
            {builtInRankers.length > 0 && <BuiltInRankerTable onView={setViewingBuiltIn} rankers={builtInRankers} />}
        </VertBox>
    );
}
